import { useState, useEffect, useCallback } from "react";
import {
  type ECOState,
  ECO_STATE_LABELS,
  ECO_STATE_COLORS,
  type LifecycleState,
  STATE_LABELS,
  STATE_COLORS,
} from "./lifecycle";
import ApprovalSection from "./ApprovalSection";

/* ── Types ───────────────────────────────────────── */

interface ECOSummary {
  id: string;
  title: string;
  state: ECOState;
  priority: string;
  created_at: string;
  requested_by_name: string;
}

interface ECOItem {
  id: string;
  eco_id: string;
  doc_id: string;
  reason: string | null;
  doc_name: string;
  part_number: string;
  doc_state: LifecycleState;
}

interface ECODetail {
  id: string;
  title: string;
  description: string | null;
  state: ECOState;
  priority: string;
  requested_by: string;
  requested_by_name: string;
  created_at: string;
  updated_at: string;
  items: ECOItem[];
}

interface DocumentOption {
  id: string;
  name: string;
  part_number: string;
}

const PRIORITY_COLORS: Record<string, string> = {
  low: "#666",
  normal: "#999",
  high: "#b89a20",
  critical: "#c44",
};

/* ── Component ───────────────────────────────────── */

export default function ECOPanel() {
  const [view, setView] = useState<"list" | "create" | "detail">("list");
  const [ecos, setEcos] = useState<ECOSummary[]>([]);
  const [selectedEco, setSelectedEco] = useState<ECODetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Create form
  const [createTitle, setCreateTitle] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [createPriority, setCreatePriority] = useState("normal");
  const [creating, setCreating] = useState(false);

  // Add document form
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [docSearch, setDocSearch] = useState("");
  const [docOptions, setDocOptions] = useState<DocumentOption[]>([]);
  const [addDocReason, setAddDocReason] = useState("");

  // Fetch ECO list
  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/api/ecos", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load ECOs");
      setEcos(await res.json());
      setError("");
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  // Fetch ECO detail
  const fetchDetail = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/ecos/${id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load ECO");
      const data = await res.json();
      setSelectedEco(data);
      setError("");
    } catch (err: any) {
      setError(err.message);
    }
  }, []);

  // Search docs for add form
  useEffect(() => {
    if (!docSearch.trim()) {
      setDocOptions([]);
      return;
    }
    const controller = new AbortController();
    fetch(`/api/documents?search=${encodeURIComponent(docSearch)}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((data) => setDocOptions(data))
      .catch(() => {});
    return () => controller.abort();
  }, [docSearch]);

  /* ── Actions ───────────────────────────────────── */

  const handleCreate = useCallback(async () => {
    if (!createTitle.trim()) return;
    setCreating(true);
    setError("");
    try {
      const res = await fetch("/api/ecos", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: createTitle,
          description: createDesc || undefined,
          priority: createPriority,
        }),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        throw new Error(b.error || `Create failed (${res.status})`);
      }
      const eco = await res.json();
      setCreateTitle("");
      setCreateDesc("");
      setCreatePriority("normal");
      setView("detail");
      await fetchDetail(eco.id);
      await fetchList();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }, [createTitle, createDesc, createPriority, fetchDetail, fetchList]);

  const handleTransition = useCallback(
    async (targetState: string) => {
      if (!selectedEco) return;
      setError("");
      try {
        const res = await fetch(`/api/ecos/${selectedEco.id}/transition`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ state: targetState }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Transition failed (${res.status})`);
        }
        await fetchDetail(selectedEco.id);
        await fetchList();
      } catch (err: any) {
        setError(err.message);
      }
    },
    [selectedEco, fetchDetail, fetchList]
  );

  const handleAddDoc = useCallback(
    async (docId: string) => {
      if (!selectedEco) return;
      setError("");
      try {
        const res = await fetch(`/api/ecos/${selectedEco.id}/items`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ doc_id: docId, reason: addDocReason || undefined }),
        });
        if (!res.ok) {
          const b = await res.json().catch(() => ({}));
          throw new Error(b.error || `Add failed (${res.status})`);
        }
        setShowAddDoc(false);
        setDocSearch("");
        setDocOptions([]);
        setAddDocReason("");
        await fetchDetail(selectedEco.id);
      } catch (err: any) {
        setError(err.message);
      }
    },
    [selectedEco, addDocReason, fetchDetail]
  );

  const handleRemoveDoc = useCallback(
    async (itemId: string) => {
      if (!selectedEco) return;
      setError("");
      try {
        const res = await fetch(`/api/ecos/${selectedEco.id}/items/${itemId}`, {
          method: "DELETE",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Remove failed");
        await fetchDetail(selectedEco.id);
      } catch (err: any) {
        setError(err.message);
      }
    },
    [selectedEco, fetchDetail]
  );

  /* ── Transition buttons ────────────────────────── */

  const getTransitionButtons = (state: ECOState) => {
    const map: Record<ECOState, { label: string; target: string }[]> = {
      draft: [{ label: "Submit", target: "submitted" }],
      submitted: [
        { label: "Approve", target: "approved" },
        { label: "Reject", target: "draft" },
      ],
      approved: [{ label: "Implement", target: "implemented" }],
      implemented: [{ label: "Close", target: "closed" }],
      closed: [],
    };
    return map[state] || [];
  };

  /* ── Renders ───────────────────────────────────── */

  if (loading && view === "list") {
    return (
      <div className="bom-panel">
        <div className="release-loading">Loading ECOs...</div>
      </div>
    );
  }

  // Create form
  if (view === "create") {
    return (
      <div className="bom-panel">
        <div className="bom-actions">
          <div className="release-section-header">New Change Order</div>
        </div>
        {error && <div className="release-error">{error}</div>}
        <div className="bom-add-form">
          <div className="release-field">
            <label className="release-field-label">Title *</label>
            <input
              className="release-field-input"
              type="text"
              placeholder="ECO title..."
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              autoFocus
            />
          </div>
          <div className="release-field">
            <label className="release-field-label">Description</label>
            <textarea
              className="release-field-input"
              placeholder="Describe the change..."
              value={createDesc}
              onChange={(e) => setCreateDesc(e.target.value)}
              rows={3}
              style={{ resize: "vertical", fontFamily: "var(--mono)" }}
            />
          </div>
          <div className="release-field">
            <label className="release-field-label">Priority</label>
            <select
              className="release-field-input"
              value={createPriority}
              onChange={(e) => setCreatePriority(e.target.value)}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button
              className="release-save-btn"
              onClick={handleCreate}
              disabled={creating || !createTitle.trim()}
            >
              {creating ? "Creating..." : "Create ECO"}
            </button>
            <button
              className="bom-btn"
              onClick={() => {
                setView("list");
                setError("");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Detail view
  if (view === "detail" && selectedEco) {
    const stateColors = ECO_STATE_COLORS[selectedEco.state];

    return (
      <div className="bom-panel">
        {/* Header */}
        <div className="bom-actions">
          <button
            className="bom-btn"
            onClick={() => {
              setView("list");
              setSelectedEco(null);
              setError("");
            }}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M10 3L5 8l5 5" />
            </svg>
            Back
          </button>
        </div>

        {error && <div className="release-error">{error}</div>}

        {/* ECO info */}
        <div style={{ padding: "8px 12px" }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-bright)", marginBottom: 4 }}>
            {selectedEco.title}
          </div>
          {selectedEco.description && (
            <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 8, whiteSpace: "pre-wrap" }}>
              {selectedEco.description}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <span
              className="bom-state-badge"
              style={{ background: stateColors.bg, color: stateColors.fg }}
            >
              {ECO_STATE_LABELS[selectedEco.state]}
            </span>
            <span
              style={{
                fontSize: 11,
                color: PRIORITY_COLORS[selectedEco.priority] || "#999",
                fontWeight: 600,
                textTransform: "uppercase",
              }}
            >
              {selectedEco.priority}
            </span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
              by {selectedEco.requested_by_name}
            </span>
          </div>

          {/* Transition buttons */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {getTransitionButtons(selectedEco.state).map((btn) => (
              <button
                key={btn.target}
                className="bom-btn"
                onClick={() => handleTransition(btn.target)}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* Affected Documents */}
        <div style={{ padding: "4px 12px" }}>
          <div className="release-section-header" style={{ marginBottom: 6 }}>
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M3 2h7l3 3v9H3z" />
              <path d="M10 2v3h3" />
            </svg>
            Affected Documents
            <button
              className="bom-btn"
              style={{ marginLeft: "auto", fontSize: 11 }}
              onClick={() => setShowAddDoc(!showAddDoc)}
            >
              + Add
            </button>
          </div>

          {showAddDoc && (
            <div className="bom-add-form" style={{ marginBottom: 8 }}>
              <div className="release-field">
                <label className="release-field-label">Search Document</label>
                <input
                  className="release-field-input"
                  type="text"
                  placeholder="Search by name or part number..."
                  value={docSearch}
                  onChange={(e) => setDocSearch(e.target.value)}
                  autoFocus
                />
              </div>
              {docOptions.length > 0 && (
                <div className="bom-doc-options">
                  {docOptions.map((d) => (
                    <button
                      key={d.id}
                      className="bom-doc-option"
                      onClick={() => handleAddDoc(d.id)}
                    >
                      <span className="bom-doc-option-name">{d.name}</span>
                      {d.part_number && (
                        <span className="bom-doc-option-pn">{d.part_number}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <div className="release-field">
                <label className="release-field-label">Reason</label>
                <input
                  className="release-field-input"
                  type="text"
                  placeholder="Optional reason..."
                  value={addDocReason}
                  onChange={(e) => setAddDocReason(e.target.value)}
                />
              </div>
            </div>
          )}

          {selectedEco.items.length === 0 ? (
            <div className="version-empty" style={{ padding: "8px 0" }}>
              No affected documents
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {selectedEco.items.map((item) => {
                const docColors = STATE_COLORS[item.doc_state];
                return (
                  <div
                    key={item.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 6px",
                      borderRadius: "var(--radius-sm)",
                      background: "var(--bg-surface)",
                      fontSize: 12,
                    }}
                  >
                    <span style={{ flex: 1, color: "var(--text-primary)" }}>
                      {item.doc_name}
                    </span>
                    {item.part_number && (
                      <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
                        {item.part_number}
                      </span>
                    )}
                    <span
                      className="bom-state-badge"
                      style={{
                        background: docColors?.bg,
                        color: docColors?.fg,
                        fontSize: 10,
                      }}
                    >
                      {STATE_LABELS[item.doc_state] || item.doc_state}
                    </span>
                    <button
                      className="bom-delete-btn"
                      onClick={() => handleRemoveDoc(item.id)}
                      title="Remove"
                    >
                      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M4 4l8 8M12 4l-8 8" />
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Approvals */}
        <div style={{ padding: "4px 12px" }}>
          <ApprovalSection
            resourceType="eco"
            resourceId={selectedEco.id}
            onUpdate={() => fetchDetail(selectedEco.id)}
          />
        </div>
      </div>
    );
  }

  // List view (default)
  return (
    <div className="bom-panel">
      <div className="bom-actions">
        <div className="release-section-header">
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
            <path d="M3 3h10v4H3zM5 7v6M11 7v6M3 13h10" />
          </svg>
          Change Orders
        </div>
        <div className="bom-actions-buttons">
          <button
            className="bom-btn"
            onClick={() => {
              setView("create");
              setError("");
            }}
          >
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M8 3v10M3 8h10" />
            </svg>
            New ECO
          </button>
        </div>
      </div>

      {error && <div className="release-error">{error}</div>}

      {ecos.length === 0 ? (
        <div className="version-empty">No change orders yet</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 2, padding: "0 8px 8px" }}>
          {ecos.map((eco) => {
            const colors = ECO_STATE_COLORS[eco.state];
            return (
              <button
                key={eco.id}
                className="bom-doc-option"
                style={{ textAlign: "left" }}
                onClick={() => {
                  setView("detail");
                  setError("");
                  fetchDetail(eco.id);
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 6, width: "100%" }}>
                  <span
                    className="bom-state-badge"
                    style={{ background: colors.bg, color: colors.fg, fontSize: 10 }}
                  >
                    {ECO_STATE_LABELS[eco.state]}
                  </span>
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: PRIORITY_COLORS[eco.priority] || "#999",
                      flexShrink: 0,
                    }}
                    title={eco.priority}
                  />
                  <span style={{ flex: 1, color: "var(--text-primary)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {eco.title}
                  </span>
                  <span style={{ fontSize: 10, color: "var(--text-muted)", flexShrink: 0 }}>
                    {new Date(eco.created_at).toLocaleDateString()}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
